import AddIcon from '@mui/icons-material/Add'
import { Autocomplete, Box, IconButton, Popover, TextField, Tooltip } from '@mui/material'
import { useUpdateDocumentTags } from '@src/api/document-api'
import TagChip from '@src/components/TagChip'
import { useUser } from '@src/providers/UserProvider'
import { DocsAutomation, EProcessingStatus } from '@src/types/automationDocument'
import { PREDEFINED_TAGS } from '@src/types/constants'
import { devtools } from 'globals'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TagsCellProps {
  document: DocsAutomation
}

const TagsCell = ({ document }: TagsCellProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const updateTagsMutation = useUpdateDocumentTags()

  const isDisabled = document.processingStatus === EProcessingStatus.Finalized

  const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleAddTag = async (tag: string) => {
    if (!tag || document.tags?.includes(tag.toLowerCase())) {
      handleClose()
      return
    }

    try {
      await updateTagsMutation.mutateAsync({
        documentId: document.id,
        versionTag: document.versionTag,
        tags: [...(document.tags || []), tag.toLowerCase()],
      })
      handleClose()
    } catch {
      enqueueSnackbar(t('common.messages.error'), { variant: 'error' })
    }
  }

  const handleDeleteTag = async (tagToDelete: string) => {
    try {
      await updateTagsMutation.mutateAsync({
        documentId: document.id,
        versionTag: document.versionTag,
        tags: (document.tags || []).filter((tag) => tag !== tagToDelete),
      })
    } catch {
      enqueueSnackbar(t('common.messages.error'), { variant: 'error' })
    }
  }

  const availableTags: string[] = PREDEFINED_TAGS.filter((tag) => !document.tags?.includes(tag))
  if (devtools && user) {
    const assignationTag = `a:${user.name.toLocaleLowerCase()}`
    if (!document.tags?.includes(assignationTag)) {
      availableTags.push(assignationTag)
    }
  }

  const open = Boolean(anchorEl)

  return (
    <Box data-tour="tags-cell" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', height: '100%' }}>
      {document.tags?.map((tag) => (
        <Tooltip key={tag} title={t(`enum.tags.${tag}` as const, { defaultValue: tag })}>
          <TagChip
            tag={tag}
            size="small"
            sx={{ maxHeight: 20, overflow: 'hidden' }}
            onDelete={isDisabled ? undefined : () => void handleDeleteTag(tag)}
            disabled={updateTagsMutation.isPending}
          />
        </Tooltip>
      ))}
      {!isDisabled && (
        <>
          <IconButton
            size="small"
            onClick={handleAddClick}
            disabled={updateTagsMutation.isPending}
            sx={{ padding: 0.25, flexShrink: 0 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: 1, minWidth: 200 }}>
              <Autocomplete
                freeSolo
                options={availableTags}
                getOptionLabel={(option) => t(`enum.tags.${option}` as const, { defaultValue: option })}
                onChange={(_event, value) => {
                  if (value) {
                    void handleAddTag(value)
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder={t('docsAutomation.addTag')} autoFocus />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <TagChip tag={option} size="small" sx={{ mr: 1 }} />
                  </li>
                )}
              />
            </Box>
          </Popover>
        </>
      )}
    </Box>
  )
}

export default TagsCell
